// UserDetailPage.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { adminApi } from "./adminApi";
import "./UserDetailPage.css";

function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(totalSeconds) {
  if (!totalSeconds || totalSeconds <= 0) return "0m";
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

export default function UserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [sectionSummary, setSectionSummary] = useState([]);
  const [interactionSummary, setInteractionSummary] = useState([]);
  const [activeIpCount, setActiveIpCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDetail = useCallback(async () => {
    setError("");
    try {
      const [sessionData, summaryData, interactionData, ipData] = await Promise.all([
        adminApi.getSessions(userId),
        adminApi.getSectionSummary(userId),
        adminApi.getInteractionSummary(userId),
        adminApi.getActiveIpCount(userId),
      ]);
      setSessions(sessionData);
      setSectionSummary(summaryData);
      setInteractionSummary(interactionData);
      setActiveIpCount(ipData.distinctActiveIps);
    } catch (err) {
      setError(err.message || "Could not load client detail.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const totalTimeSeconds = sessions.reduce((sum, s) => sum + (s.durationSeconds || 0), 0);
  const maxSectionSeconds = sectionSummary.length
    ? Math.max(...sectionSummary.map((s) => s.total_seconds || 0))
    : 0;

  return (
    <div className="udp-page">
      <button className="udp-back" onClick={() => navigate("/admin/dashboard")}>
        ← Back to all clients
      </button>

      {loading ? (
        <div className="udp-empty">Loading…</div>
      ) : error ? (
        <div className="udp-error">{error}</div>
      ) : (
        <>
          <header className="udp-header">
            <h1 className="udp-title">Client Activity</h1>
            {activeIpCount > 1 && (
              <span className="udp-flag" title="This client currently has active sessions from more than one IP address">
                ⚠ {activeIpCount} active sessions from different locations
              </span>
            )}
          </header>

          <div className="udp-summary-cards">
            <div className="udp-card">
              <div className="udp-card-value">{sessions.length}</div>
              <div className="udp-card-label">Total sessions</div>
            </div>
            <div className="udp-card">
              <div className="udp-card-value">{formatDuration(totalTimeSeconds)}</div>
              <div className="udp-card-label">Total time on site</div>
            </div>
            <div className="udp-card">
              <div className="udp-card-value">
                {sessions[0] ? formatDateTime(sessions[0].loginTime) : "—"}
              </div>
              <div className="udp-card-label">Most recent visit</div>
            </div>
          </div>

          <section className="udp-section">
            <h2 className="udp-section-title">Section Engagement</h2>
            {sectionSummary.length === 0 ? (
              <div className="udp-empty-small">No section activity recorded yet.</div>
            ) : (
              <div className="udp-section-list">
                {sectionSummary.map((s) => (
                  <div className="udp-section-row" key={s.section_name}>
                    <div className="udp-section-name">{s.section_name}</div>
                    <div className="udp-section-bar-track">
                      <div
                        className="udp-section-bar-fill"
                        style={{
                          width: maxSectionSeconds
                            ? `${(s.total_seconds / maxSectionSeconds) * 100}%`
                            : "0%",
                        }}
                      />
                    </div>
                    <div className="udp-section-time">{formatDuration(s.total_seconds)}</div>
                    <div className="udp-section-visits">{s.visit_count} visit{s.visit_count === 1 ? "" : "s"}</div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="udp-section">
            <h2 className="udp-section-title">Session History</h2>
            {sessions.length === 0 ? (
              <div className="udp-empty-small">No sessions recorded yet.</div>
            ) : (
              <div className="udp-table-wrap">
                <table className="udp-table">
                  <thead>
                    <tr>
                      <th>Login</th>
                      <th>Logout</th>
                      <th>Duration</th>
                      <th>IP Address</th>
                      <th>Device</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((s) => (
                      <tr key={s.id}>
                        <td>{formatDateTime(s.loginTime)}</td>
                        <td>{s.logoutTime ? formatDateTime(s.logoutTime) : "Active now"}</td>
                        <td>{formatDuration(s.durationSeconds)}</td>
                        <td className="udp-mono">{s.ipAddress || "—"}</td>
                        <td className="udp-device">{s.userAgent || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}